import { ApexOptions } from 'apexcharts'
import { FeatureKey } from 'common/constants/permission'
import { fNumber } from 'common/utils/formatNumber'
import { useIsFishSpecie, useSpeciesName } from 'modules/pond/api/selector'
import { PondStatus } from 'modules/pond/constants'
import { usePermitByFeature } from 'modules/user/api/selector'
import { ReactNode } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { PATH_DASHBOARD } from 'routes/paths'

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Card, Link, Skeleton, Stack, Tooltip, Typography, useTheme } from '@mui/material'

import { Donut } from '../Cost/styled'

interface ProductionChartProps {
  title: ReactNode
  isLoading: boolean
  unit?: string
  status: PondStatus | undefined
  isSupport?: boolean
  hasGrowthRecord?: boolean
  /** false → no feeding data, yield can't be estimated (DOF-1357) */
  yieldEstimatable?: boolean
  breedName?: string
  soldCount?: number
  inPondCount?: number
  data: {
    soldShrimpWeightInKg: number
    shrimpWeightInPondInKg: number
    totalShrimpWeightInKg: number
    percentageOfSoldShrimpWeight: number
    percentageOfShrimpWeightInPond: number
  }
}

const ProductionChart = ({
  data,
  title,
  unit,
  isLoading,
  status,
  breedName,
  soldCount,
  inPondCount,
  isSupport: isSupportProp = false,
  hasGrowthRecord,
  yieldEstimatable,
}: ProductionChartProps) => {
  const { t } = useTranslation('pond')
  const theme = useTheme()
  const navigate = useNavigate()
  const { farmId, pondId } = useParams()

  const speciesName = useSpeciesName() ?? ''
  const isFishSpecie = useIsFishSpecie()
  const allowGrowth = usePermitByFeature(FeatureKey.Growth)

  // DOF-1357: no feeding data comes before every other support check and
  // shows the same "–" layout with the food message.
  const noFoodData = yieldEstimatable === false
  const isSupport = isSupportProp || noFoodData
  const showGrowthWarning =
    !noFoodData && status !== PondStatus.Completed && isSupport && hasGrowthRecord === false
  const hideEstimate = noFoodData || showGrowthWarning

  const chartData = hideEstimate
    ? [0, 1]
    : [data.soldShrimpWeightInKg || 0, data.shrimpWeightInPondInKg || 0]

  const options: ApexOptions = {
    labels: [
      t('pond:animal.catch', { species: speciesName }),
      t('pond:animal.inpond', { species: speciesName }),
    ],
    states: {
      hover: { filter: { type: 'none' } },
      active: { filter: { type: 'none' } },
    },
    legend: { show: false },
    chart: {
      events: {
        dataPointSelection: () => {},
        legendClick: () => {},
      },
    },
    tooltip: {
      custom: function ({ series, seriesIndex }) {
        const value = series[seriesIndex]
        const backgroundColor = 'black'
        const textColor = 'white'
        const tooltipText =
          seriesIndex === 0
            ? t('animal.catch', { species: speciesName })
            : t('animal.inpond', { species: speciesName })

        return `
            <div style="padding: 10px; background: ${backgroundColor}; color: ${textColor};">
              <p style="color: ${textColor};">${tooltipText}: ${fNumber(value)}</p>
            </div>
          `
      },
    },
    plotOptions: {
      pie: {
        expandOnClick: false,
        donut: {
          size: isSupport ? '85%' : '100%',
          labels: {
            value: {
              formatter: (v: string) =>
                hideEstimate ? '-' : `${fNumber(Number(v))} ${t('pond:kg.thai.unit')}`,
            },
            total: {
              label: t('total.production'),
              formatter: (w: { globals: { seriesTotals: number[] } }) => {
                if (hideEstimate) {
                  return '-'
                }
                const sum = w.globals.seriesTotals.reduce((a, b) => a + b, 0)
                return `${fNumber(sum)} ${t('pond:kg.thai.unit')}`
              },
            },
          },
        },
      },
    },
    colors: hideEstimate
      ? [theme.palette.grey[200], theme.palette.grey[200]]
      : [theme.palette.primary.main, theme.palette.primary.light],
  }

  const renderRow = ({
    label,
    percentage,
    weight,
    count,
    color,
    tooltip,
    hidden,
  }: {
    label: string | React.ReactNode
    percentage?: number
    weight?: number
    count?: number
    color: string
    tooltip?: string
    /** show "–" instead of the numbers */
    hidden: boolean
  }) => (
    <Box display="flex" alignItems="center" width="100%">
      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ fontSize: '30px', marginRight: 8, color }}>{'•'}</span>
        {label}
        {tooltip && !isFishSpecie && (
          <Tooltip title={tooltip} arrow>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', ml: 0.5 }} />
          </Tooltip>
        )}
        {percentage !== undefined && (
          <Typography variant="caption" sx={{ marginLeft: 1 }}>
            ({fNumber(percentage || 0)}%)
          </Typography>
        )}
      </Typography>
      <Box flexGrow={1} />
      {hidden ? (
        <Typography>-</Typography>
      ) : (
        <Stack alignItems="flex-end">
          {weight !== undefined && (
            <Typography>
              {fNumber(weight || 0)}
              <Typography variant="body1" component="span" sx={{ marginLeft: 1 }}>
                {unit}
              </Typography>
            </Typography>
          )}
          {count !== undefined && (
            <Typography>
              {fNumber(count || 0)}
              <Typography variant="body1" component="span" sx={{ marginLeft: 1 }}>
                {t('pond:shrimp.unit')}
              </Typography>
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  )

  return (
    <Card
      sx={{
        width: '100%',
        height: '100%',
        p: 2,
        boxShadow: 'none',
        border: '1px solid',
        borderColor: theme.palette.grey[300],
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="h5">{title}</Typography>
        {!isFishSpecie && (
          <Tooltip title={t('pond:production.tooltip')} arrow>
            <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
          </Tooltip>
        )}
      </Stack>

      {noFoodData && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1.5 }}>
          <ErrorOutlineIcon sx={{ fontSize: 18, color: 'error.main', mt: 0.25 }} />
          <Typography variant="body2" sx={{ color: 'error.main' }}>
            {t('pond:yield.no.food.data.warning')}
          </Typography>
        </Stack>
      )}

      {showGrowthWarning && (
        <Stack direction="row"  alignItems="center" spacing={0.5} sx={{ mt: 1.5 }}>
          <ErrorOutlineIcon sx={{ fontSize: 18, color: 'error.main', mt: 0.25 }} />
          <Typography variant="body2" sx={{ color: 'error.main' }}>
            {t('pond:no.growth.record.warning.prefix', { species: speciesName })}
            {allowGrowth ? (
              <Link
                component="button"
                type="button"
                underline="hover"
                variant="subtitle1"
                sx={{ fontWeight: 700, verticalAlign: 'baseline' }}
                onClick={() => navigate(PATH_DASHBOARD.ponds.growth(farmId ?? '', pondId ?? ''))}
              >
                {t('pond:growth')}
              </Link>
            ) : (
              <Typography component="span" variant="subtitle1" sx={{ fontWeight: 700 }}>
                {t('pond:growth')}
              </Typography>
            )}
            {t('pond:no.growth.record.warning.suffix', { species: speciesName })}
          </Typography>
        </Stack>
      )}

      <Stack
        sx={{
          ...(isSupport &&
            !hideEstimate && {
              justifyContent: 'space-between',
              height: '100%',
            }),
        }}
        pt={hideEstimate ? 3 : 8}
        pb={3}
      >
        <>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={3}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <Donut chartData={chartData} options={options} width={350} />
              {hideEstimate && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 'calc(50% + 40px)',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1.5,
                    py: 0.5,
                    whiteSpace: 'nowrap',
                    bgcolor: 'error.lighter',
                    border: '1px solid',
                    borderColor: 'error.light',
                    borderRadius: 999,
                  }}
                >
                  <ErrorOutlineIcon sx={{ fontSize: 14, color: 'error.main' }} />
                  <Typography variant="caption" fontWeight={600} sx={{ color: 'error.main' }}>
                    {noFoodData
                      ? t('pond:yield.no.food.data.badge')
                      : t('pond:no.shrimp.size.badge', { species: speciesName })}
                  </Typography>
                </Box>
              )}
            </Box>
          </Stack>
          {status !== PondStatus.Completed && isSupport && (
            <Stack
              display="flex"
              justifyContent="flex-start"
              alignItems="stretch"
              direction="column"
              gap={2}
              sx={{ px: 2, pt: 1 }}
            >
              {renderRow({
                label: <Trans i18nKey={'pond:animal.catch'} values={{ species: speciesName }} />,
                color: theme.palette.primary.main,
                weight: data.soldShrimpWeightInKg,
                count: soldCount,
                tooltip: t('pond:animal.catch.tooltip'),
                // Sold weight is real data; only the size-less case hides it.
                hidden: showGrowthWarning,
              })}

              {renderRow({
                label: <Trans i18nKey={'pond:animal.inpond'} values={{ species: speciesName }} />,
                color: theme.palette.primary.light,
                weight: data.shrimpWeightInPondInKg,
                count: inPondCount,
                tooltip: t('pond:animal.inpond.tooltip'),
                hidden: hideEstimate,
              })}
            </Stack>
          )}

          {status !== PondStatus.Completed && !isSupport && (
            <Stack
              spacing={2}
              justifyContent="center"
              alignItems="center"
              sx={{
                py: 2,
                height: 'auto',
              }}
            >
              <Stack>
                {isLoading || !breedName ? (
                  <Skeleton width="300px" />
                ) : (
                  <Typography
                    variant="subtitle1"
                    style={{ color: theme.palette.text.secondary, textAlign: 'center' }}
                  >
                    <Trans i18nKey="pond:no.support.estimate" values={{ breed: breedName }} />
                  </Typography>
                )}
              </Stack>
            </Stack>
          )}
        </>
      </Stack>
    </Card>
  )
}

export default ProductionChart
